import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download, Loader2, FileSignature } from "lucide-react";
import { api } from "../../lib/api";
import { handlePdfError } from "../../lib/pdf";
import { useAuth } from "../../context/AuthContext";
import { opCost, creditLabel, tierName } from "../../lib/pricing";
import { useOnline } from "../../hooks/usePWA";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import ToolIntro from "./ToolIntro";
import PreviewUnlock from "./PreviewUnlock";

const TYPES = [
  { key: "agreement_sow", label: "Scope of Work", testid: "agreement-select-sow" },
  { key: "agreement_release", label: "Content Release", testid: "agreement-select-release" },
  { key: "agreement_sponsorship", label: "Sponsorship", testid: "agreement-select-sponsorship" },
];

const DEMO = {
  agreement_sow: { deliverables: "• Logo design (3 concepts)\n• 2 revision rounds\n• Final source files", fee: "$1,500" },
  agreement_release: { deliverables: "Podcast episode appearance — audio & video", fee: "None (voluntary)" },
  agreement_sponsorship: { deliverables: "• 1 Instagram Reel\n• 2 Stories with link\n• Usage rights for 30 days", fee: "$2,000" },
};

export default function AgreementsTab() {
  const [type, setType] = useState("agreement_sow");
  const [data, setData] = useState({
    creator_name: "Your Name / Studio", client_name: "Client / Brand",
    date: "", due_date: "2 weeks from signing", ...DEMO.agreement_sow,
  });
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const online = useOnline();

  const cost = opCost(user, `template:${type}`);
  const upd = (f, v) => setData((d) => ({ ...d, [f]: v }));
  const pick = (k) => { setType(k); setData((d) => ({ ...d, ...DEMO[k] })); };

  const generate = async () => {
    setLoading(true);
    try {
      const label = TYPES.find((t) => t.key === type)?.label || "Agreement";
      const title = `${label} — ${data.client_name}`;
      const res = await api.post("/pdf/template", { template_type: type, data, title });
      setStage(res.data);
    } catch (e) {
      if (handlePdfError(e) === "no-credits") navigate("/buy-credits");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <ToolIntro
        Icon={FileSignature}
        title="Creator Agreements & Releases"
        desc="Generate a ready-to-sign scope of work, content/appearance release, or brand sponsorship agreement. Fill the fields, we handle the legal structure and signature blocks."
        accepts="Form inputs"
        tier={tierName(`template:${type}`)}
        costLabel={creditLabel(cost)}
      />

      <div className="inline-flex bg-slate-100 rounded-full p-1 flex-wrap mb-5">
        {TYPES.map((t) => (
          <button
            key={t.key}
            data-testid={t.testid}
            onClick={() => pick(t.key)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              type === t.key ? "bg-white text-indigo-700 shadow-sm" : "text-slate-600 hover:text-slate-900"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Creator name</Label>
            <Input data-testid="agreement-creator" className="bg-white" value={data.creator_name} onChange={(e) => upd("creator_name", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Client / Brand name</Label>
            <Input data-testid="agreement-client" className="bg-white" value={data.client_name} onChange={(e) => upd("client_name", e.target.value)} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Fee</Label>
            <Input className="bg-white" value={data.fee} onChange={(e) => upd("fee", e.target.value)} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Due / Timeline</Label>
            <Input className="bg-white" value={data.due_date} onChange={(e) => upd("due_date", e.target.value)} />
          </div>
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Effective date</Label>
          <Input className="bg-white" placeholder="e.g. June 15, 2026" value={data.date} onChange={(e) => upd("date", e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs text-slate-500">Deliverables</Label>
          <Textarea className="bg-white min-h-[120px]" value={data.deliverables} onChange={(e) => upd("deliverables", e.target.value)} />
        </div>
      </div>

      <div className="mt-5 flex justify-center">
        <Button
          data-testid="generate-agreement-button"
          onClick={generate}
          disabled={loading || !online}
          className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full px-8 h-12"
        >
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
          Create preview
        </Button>
      </div>

      {stage && <PreviewUnlock stage={stage} onClose={() => setStage(null)} onUnlocked={() => setStage(null)} />}
    </div>
  );
}
